import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Pencil } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', order: 1 });
  const [loading, setLoading] = useState(true);

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => { api.get('/rules').then(r => setRules(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleSave = async () => {
    if (!form.title || !form.content) { 
      setErrorModal({
        open: true,
        title: 'Campos requeridos faltantes',
        message: 'Por favor, completa los campos: Título y Contenido.'
      });
      return; 
    }
    try {
      if (editing) { await api.put(`/rules/${editing}`, form); toast.success('Regla actualizada'); }
      else { await api.post('/rules', form); toast.success('Regla creada'); }
      setShowForm(false); setEditing(null); setForm({ title: '', content: '', order: rules.length + 1 }); load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al guardar regla';
      setErrorModal({
        open: true,
        title: editing ? 'Error al actualizar regla' : 'Error al crear regla',
        message: errorMessage
      });
    }
  };

  const handleEdit = (r) => { setEditing(r.id); setForm({ title: r.title, content: r.content, order: r.order }); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Eliminar regla?')) return; await api.delete(`/rules/${id}`); toast.success('Regla eliminada'); load(); };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-rules-page">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>Reglas del Juego</h2>
        <button onClick={() => { setEditing(null); setForm({ title: '', content: '', order: rules.length + 1 }); setShowForm(true); }} className="gold-btn px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="create-rule-button">
          <Plus size={16} /> Crear
        </button>
      </div>

      {loading ? <div className="text-center text-white/50 py-10">Cargando...</div> : rules.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">No hay reglas registradas</div>
      ) : (
        <div className="space-y-2">
          {rules.map(r => (
            <div key={r.id} className="glass-card p-4" data-testid={`rule-row-${r.order}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#F4E39E]/20 flex items-center justify-center text-[#F4E39E] text-xs font-bold">{r.order}</span>
                  <span className="text-white font-semibold text-sm">{r.title}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(r)} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-[#F4E39E]"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(r.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-white/50 text-sm pl-9">{r.content}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-[#F4E39E]" style={{ fontFamily: 'Montserrat' }}>{editing ? 'Editar Regla' : 'Crear Regla'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm">Titulo</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="rule-title-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" />
            </div>
            <div>
              <label className="text-white/60 text-sm">Contenido</label>
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} data-testid="rule-content-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none resize-none" />
            </div>
            <div>
              <label className="text-white/60 text-sm">Orden</label>
              <input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 1 })}
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" />
            </div>
            <button onClick={handleSave} className="gold-btn w-full py-3 rounded-lg font-bold" data-testid="save-rule-button">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
